// Local tooltip system - WoW-style tooltips matching game appearance

import { GemColor } from './proto/common.js';

// Editor API base URL - configure via window.EDITOR_API_URL or default to localhost
const EDITOR_API_URL = (typeof window !== 'undefined' && (window as any).EDITOR_API_URL) || `${window.location.protocol}//${window.location.hostname}:8090`;

// LRU cache size limit
const MAX_CACHE_SIZE = 3000;

interface LanguageCache {
	items: Map<number, string>;
	spells: Map<number, string>;
}

let tooltipManager: LocalTooltipManager | null = null;
const tooltipCache: Map<string, LanguageCache> = new Map();

// Request deduplication: prevents duplicate API calls for the same tooltip
const pendingRequests: Map<string, Promise<string>> = new Map();

// Clear all cached tooltips, used when language changes
function clearTooltipCache(): void {
	tooltipCache.clear();
	pendingRequests.clear();
}

// Batch preload tooltips for a list of IDs (used by gear picker modal)
async function preloadTooltipBatch(type: 'items' | 'spells', ids: number[], lang: string): Promise<void> {
	if (ids.length === 0) return;

	if (!tooltipCache.has(lang)) {
		tooltipCache.set(lang, { items: new Map(), spells: new Map() });
	}
	const langCache = tooltipCache.get(lang)!;
	const typeCache = type === 'items' ? langCache.items : langCache.spells;

	// Filter out already-cached IDs
	const uncachedIds = ids.filter(id => !typeCache.has(id));
	if (uncachedIds.length === 0) return;

	try {
		const apiUrl = `${EDITOR_API_URL}/api/tooltip/${type}/batch?ids=${uncachedIds.join(',')}&lang=${lang}`;
		const response = await fetch(apiUrl);
		if (response.ok) {
			const data = await response.json();
			for (const [idStr, html] of Object.entries(data)) {
				const id = parseInt(idStr, 10);
				if (html) {
					typeCache.set(id, html as string);
					evictIfNeeded(typeCache);
				}
			}
		}
	} catch {
		// Silently fail - tooltips will load on demand
	}
}

function evictIfNeeded(cache: Map<number, string>): void {
	while (cache.size > MAX_CACHE_SIZE) {
		const firstKey = cache.keys().next().value;
		if (firstKey !== undefined) cache.delete(firstKey);
	}
}

class LocalTooltipManager {
	private tooltipEl: HTMLElement;
	private visible = false;
	private currentX = 0;
	private currentY = 0;
	private showGeneration = 0;

	constructor() {
		this.tooltipEl = this.createTooltipElement();
		this.attachEventListeners();
		document.body.appendChild(this.tooltipEl);
	}

	private createTooltipElement(): HTMLElement {
		const el = document.createElement('div');
		el.id = 'wowhead-tooltip';
		// Base styles matching wowsims official appearance
		el.style.cssText = `
			position: absolute;
			z-index: 99999;
			display: none;
			padding: 6px 8px;
			margin: 0;
			background: #15171e;
			border: 1px solid #7fcbd8;
			border-radius: 4px;
			box-shadow: 0 4px 16px rgba(0, 0, 0, 0.6);
			color: #fff;
			font-family: 'Microsoft YaHei', 'SimHei', sans-serif;
			font-size: 13px;
			line-height: 1.5;
			max-width: 360px;
			pointer-events: none;
			min-width: 180px;
		`;
		return el;
	}

	private attachEventListeners() {
		document.addEventListener('mouseover', this.handleMouseOver.bind(this), { passive: true });
		document.addEventListener('mouseout', this.handleMouseOut.bind(this), { passive: true });
		document.addEventListener('mousemove', this.handleMouseMove.bind(this), { passive: true });
	}

	private handleMouseOver(event: MouseEvent) {
		const target = event.target as HTMLElement;
		const idInfo = this.extractId(target);
		if (idInfo) {
			this.currentX = event.clientX;
			this.currentY = event.clientY;
			this.showTooltipAsync(idInfo, target);
		}
	}

	private handleMouseOut(event: MouseEvent) {
		const target = event.target as HTMLElement;
		if (this.extractId(target)) {
			this.hideTooltip();
		}
	}

	private handleMouseMove(event: MouseEvent) {
		if (this.visible) {
			this.currentX = event.clientX;
			this.currentY = event.clientY;
			this.positionTooltip();
		}
	}

	private extractId(elem: HTMLElement): { type: 'item' | 'spell', id: number } | null {
		while (elem && elem !== document.body) {
			if (elem.dataset?.itemId) {
				return { type: 'item', id: parseInt(elem.dataset.itemId, 10) };
			}
			if (elem.dataset?.spellId) {
				return { type: 'spell', id: parseInt(elem.dataset.spellId, 10) };
			}
			if (elem.dataset?.wowhead) {
				const data = elem.dataset.wowhead;
				const itemMatch = data.match(/(?:^|&)item=(\d+)/);
				const spellMatch = data.match(/(?:^|&)spell=(\d+)/);
				if (itemMatch) return { type: 'item', id: parseInt(itemMatch[1], 10) };
				if (spellMatch) return { type: 'spell', id: parseInt(spellMatch[1], 10) };
			}
			if (elem instanceof HTMLAnchorElement && elem.href) {
				const href = elem.href;
				const itemMatch = href.match(/[?&]item=(\d+)/);
				const spellMatch = href.match(/[?&]spell=(\d+)/);
				if (itemMatch) return { type: 'item', id: parseInt(itemMatch[1], 10) };
				if (spellMatch) return { type: 'spell', id: parseInt(spellMatch[1], 10) };
			}
			elem = elem.parentElement as HTMLElement;
		}
		return null;
	}

	// Extract extra information from data-wowhead (gems and player equipped items)
	private extractExtraInfo(elem: HTMLElement): {
		gemIds: number[],
		playerItemIds: number[],
	} {
		const gemIds: number[] = [];
		const playerItemIds: number[] = [];

		while (elem && elem !== document.body) {
			if (elem.dataset?.wowhead) {
				const data = elem.dataset.wowhead;
				// Extract gems: gems=ID1:ID2:ID3
				const gemsMatch = data.match(/(?:^|&)gems=([\d:]+)/);
				if (gemsMatch) {
					gemIds.push(...gemsMatch[1].split(':').map(id => parseInt(id, 10)).filter(id => !isNaN(id) && id > 0));
				}
				// Extract player equipped items: pcs=ID1:ID2:ID3:...
				const pcsMatch = data.match(/(?:^|&)pcs=([\d:]+)/);
				if (pcsMatch) {
					playerItemIds.push(...pcsMatch[1].split(':').map(id => parseInt(id, 10)).filter(id => !isNaN(id) && id > 0));
				}
				break;
			}
			elem = elem.parentElement as HTMLElement;
		}

		return { gemIds, playerItemIds };
	}

	private async showTooltipAsync(idInfo: { type: 'item' | 'spell', id: number }, elem: HTMLElement) {
		const gen = ++this.showGeneration;
		try {
			let html = await this.getTooltipHtml(idInfo.type, idInfo.id);
			if (gen !== this.showGeneration) return;
			if (html) {
				let extraInfoHtml = '';

				html = this.processTooltipHtml(html);

				if (idInfo.type === 'item') {
					const extraInfo = this.extractExtraInfo(elem);
					html = await this.replaceGemSockets(html, extraInfo.gemIds);
					if (gen !== this.showGeneration) return;
					html = await this.processSetBonuses(html, extraInfo.playerItemIds, idInfo.id);
					if (gen !== this.showGeneration) return;
					const setHtml = await this.getSetHtml(idInfo.id, extraInfo.playerItemIds);
					if (gen !== this.showGeneration) return;

					if (setHtml) {
						extraInfoHtml = '<div class="tooltip-sep"></div>';
						extraInfoHtml += setHtml;
					}
				}

				this.tooltipEl.innerHTML = this.wrapTooltipHtml(html, extraInfoHtml);
				this.visible = true;
				this.positionTooltip();
			}
		} catch (error) {
			console.warn('Failed to load tooltip:', error);
		}
	}

	private async getTooltipHtml(type: 'item' | 'spell', id: number): Promise<string> {
		const lang = await this.getCurrentLanguage();
		const cacheKey = type === 'item' ? 'items' : 'spells';

		if (!tooltipCache.has(lang)) {
			tooltipCache.set(lang, { items: new Map(), spells: new Map() });
		}
		const langCache = tooltipCache.get(lang)!;
		const typeCache = cacheKey === 'items' ? langCache.items : langCache.spells;

		// Check cache
		if (typeCache.has(id)) {
			return typeCache.get(id)!;
		}

		// Check for in-flight request (deduplication)
		const requestKey = `${lang}:${cacheKey}:${id}`;
		if (pendingRequests.has(requestKey)) {
			return pendingRequests.get(requestKey)!;
		}

		// Create new request
		const fetchPromise = (async () => {
			try {
				const apiUrl = `${EDITOR_API_URL}/api/tooltip/${cacheKey}/${id}?lang=${lang}`;
				const response = await fetch(apiUrl);
				if (response.ok) {
					const data = await response.json();
					if (data.html) {
						typeCache.set(id, data.html);
						evictIfNeeded(typeCache);
						return data.html;
					}
				}
			} catch {
				// Editor API not available
			} finally {
				pendingRequests.delete(requestKey);
			}
			return '';
		})();

		pendingRequests.set(requestKey, fetchPromise);
		return fetchPromise;
	}

	private async getCurrentLanguage(): Promise<string> {
		const { Database } = await import('./proto_utils/database.js');
		return Database.getCurrentLanguage() || 'en';
	}

	private wrapTooltipHtml(html: string, extraInfoHtml: string = ''): string {
		return `
			<div>
				${html}
				${extraInfoHtml}
			</div>
		`;
	}

	private processTooltipHtml(html: string): string {
		// Replace all wow.zamimg.com icon URLs to use local path
		// Extract just the filename regardless of path
		// Handles http/https, any path structure

		// Replace img src attributes
		html = html.replace(/src=["']http[s]?:\/\/wow\.zamimg\.com\/.*?([^\/\?"']+)["']/g,
			'src="/wotlk/assets/icons/$1" onerror="this.src=\'/wotlk/assets/icons/inv_misc_questionmark.jpg\'"');
		// Replace background-image url() in inline styles
		// Handle both quoted and unquoted URLs
		html = html.replace(/background-image\s*:\s*url\(["']?http[s]?:\/\/wow\.zamimg\.com\/.*?([^\/\?"']+)["']?\)/gi,
			'background-image: url("/wotlk/assets/icons/$1")');
		// Also handle any other url() occurrences
		html = html.replace(/url\(["']?http[s]?:\/\/wow\.zamimg\.com\/.*?([^\/\?"']+)["']?\)/gi,
			'url("/wotlk/assets/icons/$1")');
		// Replace any remaining zamimg URLs with fallback
		html = html.replace(/http[s]?:\/\/wow\.zamimg\.com\/[^"')(?]+/g, '/wotlk/assets/icons/inv_misc_questionmark.jpg');

		return html;
	}

	// Replace empty gem sockets with actual gem icons and names
	private async replaceGemSockets(html: string, gemIds: number[]): Promise<string> {
		const { Database } = await import('./proto_utils/database.js');
		const { gemMatchesSocket } = await import('./proto_utils/gems.js');
		const { GemColor } = await import('./proto/common.js');
		const db = await Database.get();

		// Parse HTML using DOMParser
		const parser = new DOMParser();
		// Wrap in body tag to ensure proper parsing
		const doc = parser.parseFromString(`<body>${html}</body>`, 'text/html');

		if (gemIds.length === 0) {
			// Even if no gems, we still need to add background styles for empty sockets
			this.addEmptySocketBackgroundStyles(doc);
			return doc.body.innerHTML;
		}

		// Find all elements with class containing "socket-" (empty sockets)
		const socketElements: HTMLElement[] = [];
		const allElements = doc.body.getElementsByTagName('*');
		for (let i = 0; i < allElements.length; i++) {
			const el = allElements[i] as HTMLElement;
			if (el.className && el.className.includes('socket-')) {
				socketElements.push(el);
			}
		}

		// Filter out any socket bonus elements, only keep empty socket containers
		const emptySockets = socketElements.filter(el => {
			const text = el.textContent || '';
			return !text.includes('插槽奖励') && !text.includes('插槽加成') && !text.includes('Socket Bonus');
		});

		// Collect all gem matching results
		const allMatches: boolean[] = [];

		// Replace each socket in order with corresponding gem
		for (let i = 0; i < emptySockets.length && i < gemIds.length; i++) {
			const gemId = gemIds[i];
			const gem = db.lookupGem(gemId);

			// No gem in this socket (empty socket), treat as not matched
			if (!gem) {
				allMatches.push(false);
				continue;
			}

			const socketEl = emptySockets[i];
			const gemIconUrl = `/wotlk/assets/icons/${gem.icon}.jpg`;

			// Change from <a> to <span> (remove link)
			// Create new span element and copy over relevant attributes
			const span = doc.createElement('span');

			// Copy existing classes, update quality class
			let className = socketEl.className;
			// Remove existing q0, add gem quality
			className = className.replace(/\bq\d+\b/g, '').trim();
			className += ` q${gem.quality}`;
			span.className = className;

			// Set background image style for gem icon
			// WowHead style: 16x16 background icon, 18px padding left
			let existingStyle = socketEl.getAttribute('style') || '';
			// Remove any existing background-image, add our gem icon
			existingStyle = existingStyle.replace(/background-image[^;]*;?/g, '');
			const newStyle = `background-image: url(${gemIconUrl}); ${existingStyle} display: inline-block; padding-left: 18px; background-size: 16px 16px; background-repeat: no-repeat; background-position: left center;`;
			span.setAttribute('style', newStyle.trim());

			// Get gem tooltip via centralized getTooltipHtml (with caching + dedup)
			const gemTooltip = await this.getTooltipHtml('item', gemId);
			const gemText = gemTooltip ? this.extractGemTextFromTooltip(gemTooltip) : '';

			// If extraction failed, fall back to gem name
			span.textContent = gemText || gem.name;

			// Extract socket color from class
			const classList = socketEl.className.split(/\s+/);
			const socketClass = classList.find(c => c.startsWith('socket-'));
			const socketColor = socketClass ? this.socketClassToColor(socketClass) : GemColor.GemColorUnknown;

			// Replace the old element with new span
			socketEl.parentNode!.replaceChild(span, socketEl);

			// Check if gem matches socket and store result
			if (socketColor !== GemColor.GemColorUnknown) {
				const matches = gemMatchesSocket(gem, socketColor);
				allMatches.push(matches);
			} else {
				// Unknown socket color, treat as not matched
				allMatches.push(false);
			}
		}

		// If there are more empty sockets than gems (some still empty), add those as not matched
		for (let i = gemIds.length; i < emptySockets.length; i++) {
			allMatches.push(false);
		}

		// Check if all gems match their socket colors
		const allMatched = allMatches.every(match => match);

		// Find the socket bonus element and set final color
		// Search for the span that contains the socket bonus text
		// Socket bonus is always a span with the q0/q2 class already on it
		let found = false;
		const allSpans = doc.body.getElementsByTagName('span');
		for (let i = 0; i < allSpans.length && !found; i++) {
			const elem = allSpans[i] as HTMLElement;
			const text = elem.textContent || '';
			if (text.includes('插槽奖励') || text.includes('插槽加成') || text.includes('Socket Bonus')) {
				// Change quality class based on activation
				// Activated (all matched): green (q2 = green), not activated: q0 = gray
				let elemClass = elem.className;
				elemClass = elemClass.replace(/\bq\d+\b/g, '').trim();
				elem.className = (elemClass + ' ' + (allMatched ? 'q2' : 'q0')).trim();
				found = true;
				break;
			}
		}
		// If not found in spans, search all elements
		if (!found) {
			const allDocElements = doc.body.getElementsByTagName('*');
			for (let i = 0; i < allDocElements.length && !found; i++) {
				const elem = allDocElements[i] as HTMLElement;
				const text = elem.textContent || '';
				if (text.includes('插槽奖励') || text.includes('Socket Bonus')) {
					let elemClass = elem.className;
					elemClass = elemClass.replace(/\bq\d+\b/g, '').trim();
					elem.className = (elemClass + ' ' + (allMatched ? 'q2' : 'q0')).trim();
					found = true;
					break;
				}
			}
		}

		// Add background styles for any remaining empty sockets
		this.addEmptySocketBackgroundStyles(doc);

		// Serialize back to HTML string
		return doc.body.innerHTML;
	}

	// Process set bonus lines - color them based on whether the required number of items are equipped
	private async processSetBonuses(html: string, playerItemIds: number[], currentItemId: number): Promise<string> {
		if (playerItemIds.length === 0) {
			return html;
		}

		// Parse HTML
		const parser = new DOMParser();
		const doc = parser.parseFromString(`<body>${html}</body>`, 'text/html');

		// Count how many items from the same set are equipped
		const { Database } = await import('./proto_utils/database.js');
		const db = await Database.get();
		let setName: string | null = null;
		let setCount = 0;

		// Find the set name from the current item
		const currentItem = db['items'].get(currentItemId);
		if (currentItem) {
			// Check for both possible naming conventions
			setName = (currentItem as any).setName || (currentItem as any).set_name || null;
			if (setName && setName.length > 0) {
				// Count equipped items in this set
				for (const pItemId of playerItemIds) {
					const pItem = db['items'].get(pItemId);
					const pSetName = pItem ? ((pItem as any).setName || (pItem as any).set_name || null) : null;
					if (pItem && pSetName === setName) {
						setCount++;
					}
				}
			}
		}

		if (!setName || setCount === 0) {
			return html;
		}

		// Find all set bonus lines
		// - Regex matches "(N) 效果..." where N is required count
		// - Also matches the header "Set Name (0/4)" to replace the equipped count
		const setBonusLineRegex = /[(\[{](\d+)[^\d)]*[)\]}]/g;  // matches full "(N) " or "(N/X)" - g flag needed for matchAll
		const setCountRegex = /(\d+)\/(\d+)/;  // matches "0/4" in "无畏 (0/4)"
		let foundCount = 0;
		const allElements = doc.body.getElementsByTagName('*');

		// First, update the set header count "(0/5)" → "(2/5)"
		const allElementsArray = Array.from(allElements) as HTMLElement[];
		for (const elem of allElementsArray) {
			const text = elem.textContent || '';
			const countMatch = text.match(setCountRegex);
			if (countMatch && (text.includes(setName) || text.includes('套装') || text.includes('Set'))) {
				// Replace (0/4) with (2/4) directly in this element
				// Replace (0/4) with (2/4) in all text nodes
				const replaceCountInNode = (node: Node): boolean => {
					if (node.nodeType === Node.TEXT_NODE) {
						let txt = node.textContent || '';
						const cm = txt.match(setCountRegex);
						if (cm) {
							node.textContent = txt.replace(cm[0], `${setCount}/${cm[2]}`);
							return true;
						}
					}
					let found = false;
					node.childNodes.forEach(child => {
						if (replaceCountInNode(child)) found = true;
					});
					return found;
				};
				replaceCountInNode(elem);
				foundCount++;
			}
		}

		// Step 2: Color each set bonus line separately "(N) ..."

		// Convert to static array to avoid infinite loop with live HTMLCollection
		const allTexts = Array.from(doc.body.getElementsByTagName('*')) as HTMLElement[];

		for (const elem of allTexts) {
			// Only process if it contains combo/set keywords
			const text = elem.textContent || '';
			if (!text.includes('组合') && !text.includes('套装') && !text.includes('Set')) {
				continue;
			}

			// Count how many (N) are in this element
			const reqMatches = [...text.matchAll(setBonusLineRegex)];
			if (reqMatches.length === 0) continue;

			if (reqMatches.length === 1) {
				// Single requirement - just color this element
				const requiredCount = parseInt(reqMatches[0][1], 10);
				const activated = setCount >= requiredCount;
				let elemClass = elem.className;
				elemClass = elemClass.replace(/\bq\d+\b/g, '').trim();
				elem.className = (elemClass + ' ' + (activated ? 'q2' : 'q0')).trim();
				foundCount++;
			} else {
				// Multiple requirements in same text node - split to separate spans each with correct color
				// Find the text node that contains everything
				let textNode: Text | null = null;
				let parent: HTMLElement | null = elem;
				for (const child of Array.from(elem.childNodes)) {
					if (child.nodeType === Node.TEXT_NODE && child.textContent) {
						textNode = child as Text;
						break;
					}
				}

				if (textNode && parent) {
					const fullText = textNode.textContent || '';
					// Remove original text node
					parent.removeChild(textNode);
					let lastPos = 0;

					for (const match of reqMatches) {
						const start = match.index!;
						if (start > lastPos) {
							parent.appendChild(doc.createTextNode(fullText.slice(lastPos, start)));
						}

						const nextMatchIdx = reqMatches.indexOf(match) + 1;
						const end = nextMatchIdx < reqMatches.length ? reqMatches[nextMatchIdx].index! : fullText.length;

						const span = doc.createElement('span');
						span.textContent = fullText.slice(start, end);
						const requiredCount = parseInt(match[1], 10);
						const activated = setCount >= requiredCount;
						span.className = activated ? 'q2' : 'q0';
						parent.appendChild(span);
						foundCount++;
						lastPos = end;
					}

					if (lastPos < fullText.length) {
						parent.appendChild(doc.createTextNode(fullText.slice(lastPos)));
					}
				}
			}
		}

		// Highlight equipped set pieces in the set item list
		// Find all indent divs (which contain the set piece list)
		const allDocElements = doc.body.getElementsByTagName('*');
		const allDocElementsArray = Array.from(allDocElements) as HTMLElement[];

		for (const elem of allDocElementsArray) {
			// Look for div with indent class (contains the list of set pieces)
			if (elem.className && elem.className.includes('indent')) {
				// Find all anchor tags inside this div
				const links = elem.getElementsByTagName('a');
				for (const link of Array.from(links)) {
					const href = link.getAttribute('href');
					if (!href) continue;

					// Extract item ID from href: /wotlk/cn/item=40525/...
					const itemMatch = href.match(/\/item=(\d+)\//);
					if (!itemMatch) continue;

					const itemId = parseInt(itemMatch[1], 10);
					if (isNaN(itemId) || itemId <= 0) continue;

					// Check if this item is equipped by the player
					if (playerItemIds.includes(itemId)) {
						// Add q2 to both parent span and the a tag itself
						// This ensures higher CSS specificity to override div.q0 inheritance
						if (link.parentElement) {
							let parentClass = link.parentElement.className;
							parentClass = parentClass.replace(/\bq\d+\b/g, '').trim();
							link.parentElement.className = (parentClass + ' q2').trim();
						}
						// Also add q2 to the a tag to guarantee matching high-specificity selectors
						let linkClass = link.className;
						linkClass = linkClass.replace(/\bq\d+\b/g, '').trim();
						link.className = (linkClass + ' q2').trim();
					}
				}
			}
		}

		return doc.body.innerHTML;
	}

	// Convert socket class name to GemColor enum
	private socketClassToColor(socketClass: string): GemColor {
		switch (socketClass) {
			case 'socket-red': return GemColor.GemColorRed;
			case 'socket-yellow': return GemColor.GemColorYellow;
			case 'socket-blue': return GemColor.GemColorBlue;
			case 'socket-meta': return GemColor.GemColorMeta;
			case 'socket-prismatic': return GemColor.GemColorPrismatic;
			default: return GemColor.GemColorUnknown;
		}
	}

	// Add inline background-image styles for all empty sockets (since we don't have CSS classes for them)
	private addEmptySocketBackgroundStyles(doc: Document): void {
		const socketElements: HTMLElement[] = [];
		const allElements = doc.body.getElementsByTagName('*');
		for (let i = 0; i < allElements.length; i++) {
			const el = allElements[i] as HTMLElement;
			if (el.className && el.className.includes('socket-')) {
				const text = el.textContent || '';
				// Skip socket bonus lines
				if (!text.includes('插槽奖励') && !text.includes('Socket Bonus')) {
					socketElements.push(el);
				}
			}
		}

		socketElements.forEach(socketEl => {
			// Extract socket color from class (socket-blue, socket-red, etc.)
			const classList = socketEl.className.split(/\s+/);
			const socketClass = classList.find(c => c.startsWith('socket-'));
			if (!socketClass) return;

			// socket-blue → socket-blue.gif
			const socketIconUrl = `/wotlk/assets/icons/${socketClass}.gif`;

			// Add the background style
			let existingStyle = socketEl.getAttribute('style') || '';
			// Only add if no background-image already
			if (!existingStyle.includes('background-image')) {
				const newStyle = `${existingStyle} background-image: url(${socketIconUrl}); display: inline-block; padding-left: 18px; background-size: 16px 16px; background-repeat: no-repeat; background-position: left center;`;
				socketEl.setAttribute('style', newStyle.trim());
			}
		});
	}

	// Extract the main gem attribute text from gem tooltip HTML
	private extractGemTextFromTooltip(tooltip: string): string {
		// Find the gem effect line that contains the stats
		// Look for spans that contain +X StatName pattern
		const parser = new DOMParser();
		const doc = parser.parseFromString(tooltip, 'text/html');
		const spans = doc.getElementsByTagName('span');
		let gemText = '';

		// Collect all stat lines from the tooltip that start with +
		// These are the gem attributes
		for (let i = 0; i < spans.length; i++) {
			const span = spans[i];
			const text = span.textContent?.trim() || '';
			// Look for text starting with + (gem stats)
			if (text.startsWith('+') && text.length > 1) {
				if (gemText.length > 0) {
					gemText += ' ';
				}
				gemText += text;
			}
		}

		// If no spans found, try simple regex on the raw HTML
		if (!gemText) {
			const match = tooltip.match(/\+\d+[^<]+/);
			if (match) {
				gemText = match[0].trim();
			}
		}

		return gemText;
	}

	private positionTooltip() {
		const offsetX = 15;
		const offsetY = 15;

		let left = this.currentX + offsetX;
		let top = this.currentY + offsetY;

		this.tooltipEl.style.display = 'block';
		const rect = this.tooltipEl.getBoundingClientRect();

		// Flip if needed
		if (left + rect.width > window.innerWidth) {
			left = this.currentX - rect.width - offsetX;
		}
		if (top + rect.height > window.innerHeight) {
			top = this.currentY - rect.height - offsetY;
		}

		// Convert from viewport coordinates to document coordinates (add scroll offset)
		left += window.scrollX;
		top += window.scrollY;

		// Clamp to viewport boundaries to ensure tooltip stays completely visible
		const viewportRight = window.innerWidth + window.scrollX;
		const viewportBottom = window.innerHeight + window.scrollY;

		// If tooltip is wider than viewport, center it in the viewport
		if (rect.width > window.innerWidth) {
			left = window.scrollX + (window.innerWidth - rect.width) / 2;
		} else {
			// Clamp left to ensure it doesn't go out of bounds
			if (left < window.scrollX) {
				left = window.scrollX;
			} else if (left + rect.width > viewportRight) {
				left = viewportRight - rect.width;
			}
		}

		// If tooltip is taller than viewport, center it in the viewport
		if (rect.height > window.innerHeight) {
			top = window.scrollY + (window.innerHeight - rect.height) / 2;
		} else {
			// Clamp top to ensure it doesn't go out of bounds
			if (top < window.scrollY) {
				top = window.scrollY;
			} else if (top + rect.height > viewportBottom) {
				top = viewportBottom - rect.height;
			}
		}

		this.tooltipEl.style.left = `${left}px`;
		this.tooltipEl.style.top = `${top}px`;
	}

	private hideTooltip() {
		this.showGeneration++;
		this.visible = false;
		this.tooltipEl.style.display = 'none';
		this.tooltipEl.innerHTML = '';
	}

	// Generate HTML for gem information
	private async getGemsHtml(gemIds: number[]): Promise<string> {
		if (gemIds.length === 0) return '';

		const { Database } = await import('./proto_utils/database.js');
		const db = await Database.get();

		let html = '<table>';
		for (const gemId of gemIds) {
			const gem = db.lookupGem(gemId);
			if (!gem) continue;

			html += `
				<tr>
					<td><img src="/wotlk/assets/icons/${gem.icon}.jpg" onerror="this.src='/wotlk/assets/icons/inv_misc_questionmark.jpg'" alt="${gem.name}" /></td>
					<td><span class="q${gem.quality}">${gem.name}</span></td>
				</tr>
			`;
		}
		html += '</table>';

		return html;
	}

	// Generate HTML for set information
	private async getSetHtml(itemId: number, playerItemIds: number[]): Promise<string> {
		if (playerItemIds.length === 0) return '';

		const { Database } = await import('./proto_utils/database.js');
		const db = await Database.get();

		// Get the current item
		const item = db['items'].get(itemId);
		if (!item || !item['setName']) return '';

		// Count how many items from this set are equipped
		let setCount = 0;
		for (const pItemId of playerItemIds) {
			const pItem = db['items'].get(pItemId);
			if (pItem && pItem['setName'] === item['setName']) {
				setCount++;
			}
		}

		if (setCount === 0) return '';

		return `
			<table>
				<tr>
					<td colspan="2"><b>套装：</b>${item['setName']} (已装备 ${setCount} 件)</td>
				</tr>
			</table>
		`;
	}
}

function initTooltips() {
	if (tooltipManager) return;

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', initTooltips);
		return;
	}

	tooltipManager = new LocalTooltipManager();
}

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', initTooltips);
} else {
	setTimeout(initTooltips, 0);
}

export { LocalTooltipManager, preloadTooltipBatch, clearTooltipCache };
